import { SupplierType } from '@/data/subsidy/interfaces';
import { PaymentReceiptsTypes } from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/steps/invoice-form/interfaces';
import type { InvoiceFormSchema } from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/steps/invoice-form/schema';
import { extractNumbers } from '@/utils/extract-numbers';

const brDateToISO = (date: string) => {
  const [day, month, year] = date.split('/');
  return new Date(Number(year), Number(month) - 1, Number(day)).toISOString();
};

interface MapRefundRequestToFormData {
  data: InvoiceFormSchema;
  resourceApprovalUuid: string;
  refundRequestUuid?: string;
}

export const mapRefundRequestToFormData = ({
  data,
  resourceApprovalUuid,
  refundRequestUuid,
}: MapRefundRequestToFormData) => {
  const formData = new FormData();
  const isTed = data.bank_transfer_type === 'ted';
  const isPix = data.bank_transfer_type === 'pix';
  const isInternationalSupplier =
    data.supplier_type === SupplierType.InternationalSupplier;

  formData.append('resource_approval[uuid]', resourceApprovalUuid);
  formData.append('id', refundRequestUuid || '');

  formData.append(
    'refund_request[supplier_type]',
    isInternationalSupplier ? '' : data.supplier_type
  );
  formData.append('refund_request[supplier_name]', data.supplier_name);
  formData.append(
    'refund_request[supplier_document]',
    extractNumbers(data.supplier_document || '')
  );

  formData.append(
    'refund_request[international_supplier]',
    isInternationalSupplier ? 'true' : 'false'
  );

  formData.append(
    'refund_request[purchase_date]',
    brDateToISO(data.purchase_date)
  );
  formData.append(
    'refund_request[purchase_amount]',
    String(data.purchase_amount)
  );

  formData.append(
    'refund_request[payment_date]',
    brDateToISO(data.payment_date)
  );
  formData.append(
    'refund_request[payment_amount]',
    String(data.payment_amount)
  );

  formData.append(
    'refund_request[receiver_fiscal_document]',
    data.receiver_fiscal_document
  );

  formData.append(
    'refund_request[reference_month]',
    data.reference_month?.toString() ?? ''
  );

  formData.append(
    'refund_request[banking_info][holder_name]',
    data.holder_name
  );

  formData.append('refund_request[banking_info][bank]', isTed ? data.bank : '');
  formData.append(
    'refund_request[banking_info][agency]',
    isTed ? data.agency : ''
  );
  formData.append(
    'refund_request[banking_info][account_number]',
    isTed ? data.account_number : ''
  );

  formData.append(
    'refund_request[banking_info][pix_key]',
    isPix
      ? data.pix_key_type === 'cpf'
        ? extractNumbers(data.pix_key || '')
        : data.pix_key
      : ''
  );
  formData.append(
    'refund_request[banking_info][pix_type]',
    isPix ? data.pix_key_type : ''
  );

  let receiptIndex = 0;

  const appendReceipts = (files: File[], type: string) => {
    files.forEach((file) => {
      formData.append(`receipts[${receiptIndex}][receipt_type]`, type);
      formData.append(`receipts[${receiptIndex}][attachment]`, file);
      receiptIndex++;
    });
  };

  appendReceipts(data.purchase_attachments, data.purchase_receipt_type);

  appendReceipts(data.payment_attachments, data.payment_receipt_type);

  if (data.optional_files) {
    appendReceipts(data?.optional_files, PaymentReceiptsTypes.Other);
  }

  return formData;
};
